<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import router from '@/router';
import { message } from 'ant-design-vue';
import ProgressFrame from '../layout/ProgressFrame.vue';
import HorizontalContentRow from '../../components/HorizontalContentRow.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import ProjectForm from '../../components/ProjectForm.vue';
import { projectHeaderDisplay } from '../../utils/project-and-site';
import { useProjectStore } from '../../stores/projectStore';
import { ProjectFormModel, SiteFormModel } from '../../models/Project';

const props = defineProps<{
  projectId: number;
}>();

const projectStore = useProjectStore();
const projectId = computed(() => props.projectId);
const projectData = computed(() => projectStore.getProjectById(projectId.value) ?? null);
const projectForm = ref<ProjectFormModel>(new ProjectFormModel().deserialize(projectData.value));

watch(
  projectData,
  (data) => {
    projectForm.value = new ProjectFormModel().deserialize(data);
    if (projectForm.value.sites.every((site) => site.persisted)) {
      projectForm.value.sites.push(new SiteFormModel());
    }
  },
  { immediate: true },
);

const projectHeader = computed(() => {
  if (!projectData.value) return null;
  return projectHeaderDisplay(projectData.value.id, projectData.value.name);
});

const projectFormRef = ref<InstanceType<typeof ProjectForm> | null>(null);

const goToNext = async () => {
  if (!projectFormRef.value?.validateInput()) {
    // Do not proceed if there are errors
    return;
  }

  try {
    /**
     * Only add the sites that are not persisted to the backend
     */
    const newSites = projectForm.value.sites.filter((site) => !site.persisted);

    await projectStore.addSitesToProject(projectId.value, newSites);

    router.push({ name: 'ProjectSelectSite', params: { projectId: projectId.value } });
  } catch (error: unknown) {
    message.error((error as Error).message);
    return;
  }
};

/**
 * If the user refreshes the page and the project value is not found
 * Navigate to the ProjectSelect page
 */
if (!projectData.value) {
  router.push({ name: 'ProjectSelect' });
}

const sortedSites = computed(() =>
  projectData.value?.sites
    .slice() // To avoid mutating the original array
    .sort((a, b) =>
      a.address.localeCompare(b.address, 'en', {
        numeric: true, // To sort numbers correctly (ie. 87 Site before 123 Site)
        sensitivity: 'base', // To sort case-insensitively
      }),
    ),
);
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow titleTight>
    <template #title> {{ projectHeader?.heading }} </template>
    <template #subTitle> {{ projectHeader?.subheading }} </template>
    <div class="content">
      <HorizontalContentRow firstRow lastRow>
        <div class="add-site-container">
          <h3 class="row-title">Add a site</h3>
          <div class="existing-sites-container section-divider">
            <h4>Existing sites:</h4>
            <ul>
              <li v-for="site in sortedSites" :key="site.id">
                <span data-tn="existing-site-address-text">
                  {{ site.address }}
                </span>
              </li>
            </ul>
          </div>
          <ProjectForm
            ref="projectFormRef"
            :project="projectForm"
            :showProjectName="false"
            :showPersistedSites="false"
            :deletePersistedSite="false"
            :editAddressPersistedSite="false"
            firstSiteLabel="Enter a site address for this project"
            otherSiteLabel="Enter a site address for this project"
          />
        </div>
      </HorizontalContentRow>
      <div class="end-of-page">
        <ButtonPrimary block data-tn="next-btn" @click="goToNext">
          Next
          <template #iconRight>
            <ChevronRightSVG class="chevron-right-icon" />
          </template>
        </ButtonPrimary>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  margin: 0;
  padding: 10px 0;
  .content-row {
    width: 100%;
  }
}

.add-site-container {
  width: 100%;
}
.row-title {
  text-align: center;
  width: 100%;
  margin: 0;
}

.existing-sites-container {
  padding-bottom: 10px !important;
}

.section-divider {
  padding: @content-side-margin 0;
  width: 100%;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: -@content-side-margin;
    right: -@content-side-margin;
    bottom: 0;
    border-bottom: 1px solid @color-mid-tone;
  }
}

.end-of-page {
  padding: @content-side-margin;
  width: 100%;
}
</style>
